import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Banner } from '@/components/feedback/Banner'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { useClientsOverview } from '@/features/clients/api/useClientsOverview'
import { ClientMoneyTiles } from '@/features/clients/components/ClientMoneyTiles'
import { ClientsOverviewTable } from '@/features/clients/components/ClientsOverviewTable'
import { useYearParam, YearControl } from '@/features/clients/components/YearControl'
import { useCreateClient } from '@/features/directories/api/useDirectory'
import { ClientFormSheet, type ClientDraft } from '@/features/directories/components/DirectoryFormSheets'

/* The clients overview — the workbook's "Accounts Receivable Summary" as a
 * page. One row per client the caller may see, the year's figures beside the
 * whole position, and the totals as tiles above. Money is the server's
 * decision: `moneyUnavailable` says whether it is withheld (silently) or
 * failed (with a banner), and this page never consults the role itself.
 *
 * Creating a client lives here; every other record action lives on the
 * client's own page, which is where a reader is when they decide to act. */
export function ClientsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [year, setYear] = useYearParam()
  const { data, isPending, isError, error, refetch } = useClientsOverview(year)
  const [creating, setCreating] = useState(false)
  const create = useCreateClient()

  const submit = (values: ClientDraft) => {
    create.mutate(
      { ...values },
      { onSuccess: (created) => { setCreating(false); navigate(`/clients/${created.id}`) } },
    )
  }

  const header = (
    <PageHeader
      title={t('clients.title')}
      subtitle={t('clients.subtitle')}
      actions={
        <>
          <YearControl years={data?.years ?? []} value={year} onChange={setYear} label={t('clients.year.label')} />
          <Button size="sm" iconLeft={Plus} onClick={() => setCreating(true)}>
            {t('clients.new')}
          </Button>
        </>
      }
    />
  )

  if (isPending) {
    return (
      <>
        {header}
        <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
      </>
    )
  }
  if (isError) {
    return (
      <>
        {header}
        <ErrorState error={error} onRetry={() => void refetch()} />
      </>
    )
  }

  const showMoney = data.moneyUnavailable === undefined

  return (
    <>
      {header}

      {data.moneyUnavailable === 'unavailable' ? (
        <Banner tone="attention" title={t('clients.moneyUnavailable.title')}>
          {t('clients.moneyUnavailable.body')}
        </Banner>
      ) : null}

      {data.totals ? <ClientMoneyTiles money={data.totals} /> : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('clients.table.label')}</CardTitle>
        </CardHeader>
        {create.error ? (
          <div className="px-3.5 pb-2">
            <ErrorState error={create.error} onRetry={() => create.reset()} />
          </div>
        ) : null}
        <ClientsOverviewTable
          rows={data.items}
          showMoney={showMoney}
          onOpen={(row) => navigate(`/clients/${row.id}`)}
        />
      </Card>

      <ClientFormSheet
        open={creating}
        onClose={() => setCreating(false)}
        pending={create.isPending}
        onSubmit={submit}
      />
    </>
  )
}
